import { useEffect, useState } from "react";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { SettingsService } from "../../../services/api";
import { useGlobalContext } from "../../../stores/Global";
import useRefresh from "../../../hooks/useRefresh";
import { DropDownItem } from "../../molecules/Dropdown";
import { capitalizeFirstLetter } from "../../../helpers/string";
import { useTranslation } from "react-i18next";
import { useMutation, useMutationState } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import FieldLabel from "../../atoms/FieldLabel";
import {
  CreateMessageTestBodyAudienceEnum,
  SMTPTransportSentMessageInfo,
  SMTPTransportSentMessageInfoAcceptedInner,
} from "../../../services/swagger/api";

function formatRecipients(
  recipients?: Array<SMTPTransportSentMessageInfoAcceptedInner | string>,
) {
  if (!recipients?.length) return "";
  return recipients
    .map((recipient) =>
      typeof recipient === "string"
        ? recipient
        : recipient.address || recipient.name,
    )
    .filter(Boolean)
    .join(", ");
}

export default function MessagesSettingsPage() {
  const { t } = useTranslation("Settings");
  const { context } = useGlobalContext();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const [emailConfigName, setEmailConfigName] = useState<string | undefined>();
  const [configNames, setConfigNames] = useState<DropDownItem[]>([]);
  const [subject, setSubject] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [from, setFrom] = useState("");
  const [direct, setDirect] = useState("");
  const [forward, setForward] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [audience, setAudience] = useState<CreateMessageTestBodyAudienceEnum>(
    CreateMessageTestBodyAudienceEnum.Direct,
  );
  const [messageResponse, setMessageResponse] =
    useState<SMTPTransportSentMessageInfo>();

  const [loading] = useMutationState({
    filters: { mutationKey: ["settings"] },
    select: (mutation) => mutation.state.status === "pending",
  });

  const { mutate: handleTestEmail, isPending: isTestEmailLoading } =
    useMutation({
      mutationFn: async () => {
        if (!emailConfigName) return;
        const res = await SettingsService.createMessageTest({
          createMessageTestBody: {
            testEmail,
            template: emailConfigName,
            audience,
            subject,
            from,
            body:
              audience === CreateMessageTestBodyAudienceEnum.Forward
                ? forward
                : direct,
          },
        });
        return res?.data?.data;
      },
      onSuccess: (data) => {
        setMessageResponse(data);
      },
    });

  const handleSubmit = async () => {
    if (!context.settings) return;
    await SettingsService.updateSettings({
      appSettings: {
        ...context.settings,
      },
    });
    refresh();
  };

  const handleClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!context.settings || !emailConfigName) return;
    const section = emailConfigName.split(".")[0];
    const subsection = emailConfigName.split(".")[1];
    if (!section || !subsection) return;
    if (
      typeof (context.settings.messages as any)[section][subsection].enabled !==
        "boolean" ||
      typeof (context.settings.messages as any)[section][subsection].direct !==
        "string" ||
      typeof (context.settings.messages as any)[section][subsection].forward !==
        "string" ||
      typeof (context.settings.messages as any)[section][subsection].from !==
        "string" ||
      typeof (context.settings.messages as any)[section][subsection].subject !==
        "string"
    )
      return;
    (context.settings.messages as any)[section][subsection].enabled = enabled;
    (context.settings.messages as any)[section][subsection].direct = direct;
    (context.settings.messages as any)[section][subsection].forward = forward;
    (context.settings.messages as any)[section][subsection].from = from;
    (context.settings.messages as any)[section][subsection].subject = subject;
  }, [direct, forward, from, subject, enabled]);

  useEffect(() => {
    if (!context.settings || !emailConfigName) return;
    const section = emailConfigName.split(".")[0];
    const subsection = emailConfigName.split(".")[1];
    if (!section || !subsection) return;
    if (
      typeof (context.settings.messages as any)[section][subsection].enabled !==
        "boolean" ||
      typeof (context.settings.messages as any)[section][subsection].direct !==
        "string" ||
      typeof (context.settings.messages as any)[section][subsection].forward !==
        "string" ||
      typeof (context.settings.messages as any)[section][subsection].from !==
        "string" ||
      typeof (context.settings.messages as any)[section][subsection].subject !==
        "string"
    )
      return;
    setEnabled((context.settings.messages as any)[section][subsection].enabled);
    setDirect((context.settings.messages as any)[section][subsection].direct);
    setForward((context.settings.messages as any)[section][subsection].forward);
    setFrom((context.settings.messages as any)[section][subsection].from);
    setSubject((context.settings.messages as any)[section][subsection].subject);
  }, [emailConfigName]);

  useEffect(() => {
    if (!context.settings) return;
    const arr: DropDownItem[] = [];
    for (const sectionKey of Object.keys(context.settings.messages)) {
      const subsections = (context.settings.messages as any)[sectionKey];
      for (const subsectionKey of Object.keys(subsections)) {
        arr.push({
          name: `${capitalizeFirstLetter(sectionKey)} ${subsectionKey
            .split(/(?=[A-Z])/)
            .map((e) => capitalizeFirstLetter(e))
            .join(" ")}`,
          value: `${sectionKey}.${subsectionKey}`,
        });
      }
    }
    setConfigNames(arr);
  }, [context.settings]);

  return (
    <FormTemplate
      title={t("MailMessageConfiguration.Title")}
      disableCancelButton
      submitButtonText={t("Save")}
      disableEnter
      onSubmit={handleSubmit}
      onCancel={handleClose}
      confirm
      loading={loading}
    >
      <Grid container direction={"column"} rowGap={3}>
        <Grid container alignItems={"center"} rowGap={3}>
          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("ConfigurationName")} />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              field="emailConfigName"
              label={""}
              type="list"
              placeholder="None"
              value={emailConfigName}
              list={configNames}
              onChange={(value) => setEmailConfigName(value as string)}
            />
          </Grid>

          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("Enabled")} />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              field="enabled"
              label={""}
              type="switch"
              value={enabled}
              onChange={(value) => setEnabled(value as boolean)}
            />
          </Grid>

          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("EmailSubject")} required />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              field="subject"
              label={""}
              placeholder={t("SubjectPlaceholder")}
              type="string"
              required
              value={subject}
              onChange={(value) => setSubject(value as string)}
            />
          </Grid>

          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("FromEmail")} />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              field="from"
              label={""}
              placeholder={t("FromEmailPlaceholder")}
              type="string"
              value={from}
              onChange={(value) => setFrom(value as string)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormInput
              loading={loading}
              field="forward"
              label={t("Forward")}
              placeholder={t("ForwardPlaceholder")}
              multiline
              type="string"
              minRows={Math.max(
                5,
                direct.split("\n").length,
                forward.split("\n").length
              )}
              maxRows={15}
              value={forward}
              onChange={(value) => setForward(value as string)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormInput
              loading={loading}
              field="direct"
              label={t("Direct")}
              placeholder={t("DirectPlaceholder")}
              multiline
              type="string"
              minRows={Math.max(
                5,
                direct.split("\n").length,
                forward.split("\n").length
              )}
              maxRows={15}
              value={direct}
              onChange={(value) => setDirect(value as string)}
            />
          </Grid>
        </Grid>

        <Grid container alignItems={"center"} rowGap={3}>
          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>
          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("TestEmail")} />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              field="testEmail"
              label={""}
              placeholder={t("TestEmailPlaceholder")}
              type="string"
              value={testEmail}
              onChange={(value) => setTestEmail(value as string)}
            />
          </Grid>
          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("TestMessage")} />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              field="audience"
              label={""}
              type="list"
              value={audience}
              list={[
                {
                  name: t("Direct"),
                  value: CreateMessageTestBodyAudienceEnum.Direct,
                },
                {
                  name: t("Forward"),
                  value: CreateMessageTestBodyAudienceEnum.Forward,
                },
              ]}
              onChange={(value) =>
                setAudience(value as CreateMessageTestBodyAudienceEnum)
              }
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              {t("MessageTestHint")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Button
              sx={{ minWidth: 150, width: "fit-content" }}
              onClick={() => handleTestEmail()}
              variant="outlined"
              loading={isTestEmailLoading}
              disabled={!emailConfigName || !testEmail}
            >
              {t("Test")}
            </Button>
          </Grid>
          {messageResponse && (
            <Grid size={12}>
              <Box p={2} bgcolor="background.paper">
                {(
                  [
                    ["response", messageResponse.response],
                    ["messageId", messageResponse.messageId],
                    ["accepted", formatRecipients(messageResponse.accepted)],
                    ["rejected", formatRecipients(messageResponse.rejected)],
                    ["pending", formatRecipients(messageResponse.pending)],
                  ] as const
                )
                  .filter(([, message]) => message)
                  .map(([key, message]) => (
                    <Typography color="primary" key={key}>
                      {capitalizeFirstLetter(key)}
                      {":"}
                      <Typography component="span" color="success">
                        {" " + message}
                      </Typography>
                    </Typography>
                  ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
    </FormTemplate>
  );
}
